import React, {useCallback} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {useLocation, useNavigate, useParams} from 'react-router-dom';


import {FilterInput} from 'src/components/input';
import {ACCOUNT_TABS} from 'src/constants';
import {ACCESS_RIGHTS, useAdminAccessRight} from 'src/hooks/access-rights';
import {useConferenceAccountCheck} from 'src/hooks/useConferenceAccountCheck';
import {useEventTracking} from 'src/hooks/useEventTracking';
import {UsersGroupsHeader} from 'src/modules/profile/account/tabs/users-and-groups/users-groups-header';

import Button from '@pb/kit-component-button';
import {Group} from '@pb/kit-component-group';

import {
    userSearchResultStateActions,
    userSearchResultStateSelector,
} from '../../users-groups-search-result/slices/user-search-result-state.slice';

import {AddAttendee} from './add-attendee';

type Props = {
    onAddAttendee: () => void,
};

export const Header = ({onAddAttendee}: Props) => {
    const queryParams = useParams<{accountId: string}>();
    const accountId = queryParams.accountId ? +queryParams.accountId : -1;

    const dispatch = useDispatch();
    const navigate = useNavigate();
    const {pathname} = useLocation();
    const cacheKeys = useSelector(userSearchResultStateSelector);
    const isConferenceAccount = useConferenceAccountCheck();

    const {hasAccess} = useAdminAccessRight();
    const {userCreateOpen} = useEventTracking();

    const canCreateUser = hasAccess(ACCESS_RIGHTS.USER_SCREEN_CREATE);
    const canBulkCreateUser = hasAccess(ACCESS_RIGHTS.BULK_USER_IMPORT);
    const canAddConferenceUsers = hasAccess(ACCESS_RIGHTS.CONFERENCE_EDIT);

    const changeEmailFilter = useCallback((email: string) => {
        const {userEmails: _userEmails, ...other} = cacheKeys.searchCriteria;
        if (email) {
            dispatch(userSearchResultStateActions.setSearchCriteria({
                userEmails: [email],
                ...other,
            }));
        } else {
            dispatch(userSearchResultStateActions.setSearchCriteria(other));
        }
    }, [cacheKeys, dispatch]);

    const clearEmailFilter = useCallback(() => {
        const {userEmails: _userEmails, ...other} = cacheKeys.searchCriteria;

        dispatch(userSearchResultStateActions.setSearchCriteria(other));
    }, [cacheKeys, dispatch]);

    return (
        <UsersGroupsHeader activeTab="virtual">
            <Group justify="space-between">
                <FilterInput
                    id="email-filter-input"
                    width={300}
                    placeholder="Search for users by email"
                    value={(cacheKeys.searchCriteria.userEmails as string[] | undefined)?.find(Boolean) || ''}
                    onChange={changeEmailFilter}
                    onClear={clearEmailFilter}
                    timeOut={1000}
                />
                {isConferenceAccount ? (
                    <Group>
                        {canAddConferenceUsers ? (
                            <Button
                                variant="Type.SECONDARY"
                                onClick={() => {
                                    navigate(`/add-attendees/${accountId}`, {state: {prevPath: pathname}});
                                }}>
                                Bulk Import
                            </Button>
                        ) : null}
                        {canAddConferenceUsers ? <AddAttendee onAdd={onAddAttendee} accountId={accountId}/> : null}
                    </Group>
                ) : (
                    <Group>
                        {canBulkCreateUser ? (
                            <Button
                                variant="Type.SECONDARY"
                                onClick={() => navigate(
                                    `/create-new-user/${accountId}`,
                                    {state: {prevPath: pathname}},
                                )}>
                                Bulk Import
                            </Button>
                        ) : null}
                        {canCreateUser ? (
                            <Button
                                variant="Type.PRIMARY"
                                onClick={() => {
                                    userCreateOpen({place: 'Conference Users'});
                                    navigate(`/profile/account/${accountId}/${ACCOUNT_TABS.CREATE_USER.name}`);
                                }}>
                                Add User
                            </Button>
                        ) : null}
                    </Group>
                )}
            </Group>
        </UsersGroupsHeader>
    );
};
